import React, { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  CircularProgress,
  IconButton,
  makeStyles,
  Tab,
  Tabs,
  Typography,
  // Slider,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import {
  COLOR_BACKGROUND_WARM,
  COLOR_BORDER,
  COLOR_MIDDLE_GRAY,
  COLOR_ORANGE,
  COLOR_WHITE,
} from '../styles/colors';
import { PlayerEvaluationSchema } from '../types/Evaluation';
import { MOCK_PLAYER_EVALUATION } from '../services/evaluation-mock-data';
import { buildDefaultValues, transformApiResponseToFormValues, transformFormDataForApi } from '../services/evaluation-helpers';
import { savePlayerProfile } from '../services/evaluation-api';
import TabsRenderer from './evaluation/TabsRenderer';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Slider from '../atoms/Slider';
import clsx from 'clsx';
const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 620,
    width: '100%',
    height: '100%',
    backgroundColor: COLOR_WHITE,
  },
  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
  },
  evaluationReportDrawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${COLOR_BORDER}`,
  },
  evaluationReportDrawerHeaderTitle: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 600,
  },
  evaluationReportDrawerHeaderCloseButton: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: COLOR_ORANGE,
    '&:hover': {
      opacity: 0.8,
    },
  },
  tabsRoot: {
    borderBottom: `1px solid ${COLOR_BORDER}`,
  },
  tabsIndicator: {
    height: 3,
    backgroundColor: COLOR_ORANGE,
  },
  tabRoot: {
    textTransform: 'none',
    minWidth: 0,
    padding: theme.spacing(1.5, 3),
    fontSize: theme.typography.pxToRem(14),
  },
  tabSelected: {
    fontWeight: 700,
  },
  confidentialBar: {
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${COLOR_BORDER}`,
    backgroundColor: COLOR_BACKGROUND_WARM,
    fontSize: theme.typography.pxToRem(12),
  },
  scoreCard: {
    margin: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: COLOR_BACKGROUND_WARM,
    border: `1px solid ${COLOR_BORDER}`,
  },
  scoreCardRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  metricBox: {
    flex: '0 0 170px',
    minWidth: 140,
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: COLOR_WHITE,
    border: `1px solid ${COLOR_BORDER}`,
  },
  metricLabel: {
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  evalColorLabel: {
    fontSize: theme.typography.pxToRem(16),
  },
  metricValue: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 700,
  },
  colorSwatchWrapper: {
    flex: '0 0 155px',
    minWidth: 120,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  colorSwatchLabel: {
    fontSize: theme.typography.pxToRem(11),
    color: COLOR_MIDDLE_GRAY,
    marginBottom: theme.spacing(1),
  },
  colorSwatch: {
    width: 30,
    height: 30,
    borderRadius: 8,
    backgroundColor: '#ee5a46',
    border: `1px solid ${COLOR_BORDER}`,
  },
  sliderRow: {
    marginTop: theme.spacing(2),
  },
  sliderHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    fontSize: theme.typography.pxToRem(12),
  },
  sliderInfoIcon: {
    marginLeft: theme.spacing(0.5),
    fontSize: 16,
    color: COLOR_MIDDLE_GRAY,
  },
  sliderLabels: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(11),
    color: theme.palette.text.primary,
  },
  rail: {
    height: 10,
  },
  thumb: {
    height: 20,
    width: 20,
  }
}), { name: 'EvaluationDrawerContent' });

/**
 * Score card that shows read-only player metrics from the API schema.
 */
function ScoreCard({
  schema,
  classes,
}: {
  schema: PlayerEvaluationSchema;
  classes: ReturnType<typeof useStyles>;
}) {
  return (
    <>
      <Box className={classes.confidentialBar}>
        <Typography variant="body2">
          <strong>Confidential Evaluation:</strong> Only viewable by your program
        </Typography>
      </Box>

      <Box className={classes.scoreCard}>
        <Box className={classes.scoreCardRow}>
          <Box className={classes.metricBox}>
            <div className={classes.metricLabel}>Player Evaluation Score</div>
            <div className={classes.metricValue}>{schema.playerEvaluationScore}.0</div>
          </Box>
          <Box className={classes.metricBox}>
            <div className={classes.metricLabel}>Player Evaluation Code</div>
            <div className={classes.metricValue}>{schema.playerEvaluationCode}</div>
          </Box>
          <Box className={classes.colorSwatchWrapper}>
            <div className={classes.evalColorLabel}>Evaluation Color:</div>
            <div className={classes.colorSwatch} style={{backgroundColor: schema.evaluationColor }}/>
          </Box>
        </Box>
        <Box className={classes.sliderRow}>
          <div className={classes.sliderHeader}>
            <span>Player Display:</span>
            <InfoOutlinedIcon className={classes.sliderInfoIcon} />
          </div>
          <Slider
            defaultValue={0}
            step={1}
            marks={[{ value: 0 }, { value: 1 }, { value: 2 }, { value: 3 }]}
            min={0}
            max={3}
            onChange={() => console.log('clicked slider')}
            // className={clsx(classes.rail)}
          />
          <div className={classes.sliderLabels}>
            <span>All</span>
            <span>Skip</span>
            <span>Only this</span>
            <span>None</span>
          </div>
        </Box>
      </Box>
    </>
  );
}

export default function EvaluationDrawerContent({ onClose }: { onClose: () => void }) {
  const classes = useStyles();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);

  // In production, replace with data from an API call (e.g., useEffect + fetchPlayerEvaluation)
  const schema: PlayerEvaluationSchema = transformApiResponseToFormValues(MOCK_PLAYER_EVALUATION);

  const defaultValues = useMemo(() => buildDefaultValues(schema), [schema]);

  const { control, handleSubmit } = useForm({
    defaultValues,
  });

  const onSave = useCallback(
    async (formData: Record<string, any>) => {
      setIsSaving(true);
      try {
        const payload = transformFormDataForApi(formData, schema);
        const result = await savePlayerProfile(payload);
        if (result.success) {
          console.log('Player profile saved successfully');
        }
      } catch (error) {
        console.error('Failed to save player profile:', error);
      } finally {
        setIsSaving(false);
      }
    },
    [schema],
  );

  return (
    <form
      className={classes.root}
      onSubmit={handleSubmit(onSave)}
      noValidate
    >
      {/* Header */}
      <Box className={classes.evaluationReportDrawerHeader}>
        <Typography className={classes.evaluationReportDrawerHeaderTitle}>
          Evaluation
        </Typography>
        <Box display="flex" alignItems="center">
          <Box
            mr={1}
            className={classes.saveButton}
            onClick={handleSubmit(onSave)}
          >
            {isSaving ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SaveIcon fontSize="small" />
            )}
            <Box ml={0.5} fontSize={12}>
              {isSaving ? 'Saving...' : 'Save All'}
            </Box>
          </Box>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon className={classes.evaluationReportDrawerHeaderCloseButton} />
          </IconButton>
        </Box>
      </Box>

      {/* Tabs — dynamically rendered from schema */}
      <Tabs
        className={classes.tabsRoot}
        value={selectedTab}
        classes={{ indicator: classes.tabsIndicator }}
        onChange={(_event: any, value: number) => setSelectedTab(value)}
        variant="scrollable"
        scrollButtons="auto"
      >
        {schema.customTabs.map((tab, idx) => (
          <Tab
            key={tab.name}
            label={tab.name}
            value={idx}
            classes={{ root: classes.tabRoot, selected: classes.tabSelected }}
          />
        ))}
      </Tabs>

      {/* Scrollable tab content */}
      <div className={classes.scrollableContent}>
        {/* Score card shown on first tab */}
        {selectedTab === 0 && <ScoreCard schema={schema} classes={classes} />}

        {/* Dynamic fields for the selected tab */}
        {schema.customTabs.map((tab, tabIdx) => (
          <div
            key={tab.name}
            role="tabpanel"
            hidden={selectedTab !== tabIdx}
          >
            {selectedTab === tabIdx && (
              <TabsRenderer
                tab={tab}
                tabIndex={tabIdx}
                control={control}
              />
            )}
          </div>
        ))}
      </div>
    </form>
  );
}